import { useEffect, useRef } from 'react';
import './build-choreography.css';

/** Progress is written to scene styles, not React state. No pinning or hidden copy. */
export default function BuildChoreography() {
  const marker = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const root = marker.current?.closest<HTMLElement>('.build-dossier');
    if (!root) return;
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let scenes: HTMLElement[] = [];
    let frame = 0;
    const visible = new Set<HTMLElement>();
    const paint = () => {
      frame = 0;
      if (document.hidden || preference.matches) return;
      for (const scene of Array.from(visible)) {
        const rect = scene.getBoundingClientRect();
        const enter = Math.min(
          1,
          Math.max(0, (window.innerHeight - rect.top) / (window.innerHeight * 0.65))
        );
        const exit = Math.min(1, Math.max(0, -rect.top / Math.max(rect.height, 1)));
        scene.style.setProperty('--scene-enter', enter.toFixed(4));
        scene.style.setProperty('--scene-exit', exit.toFixed(4));
      }
    };
    const schedule = () => {
      if (!frame && !preference.matches && !document.hidden) frame = requestAnimationFrame(paint);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target as HTMLElement);
          else visible.delete(entry.target as HTMLElement);
        });
        schedule();
      },
      { rootMargin: '120px 0px' }
    );
    const collect = () => {
      const next = Array.from(
        root.querySelectorAll<HTMLElement>('.build-dossier-hero, .build-dossier-sections > *')
      );
      for (const scene of scenes)
        if (!next.includes(scene)) {
          observer.unobserve(scene);
          visible.delete(scene);
        }
      for (const scene of next)
        if (!scenes.includes(scene)) {
          const kind = scene.classList.contains('build-dossier-hero')
            ? 'hero'
            : scene.classList.contains('build-dossier-summary')
              ? 'specs'
              : [
                    'recognition',
                    'performance',
                    'build',
                    'timeline',
                    'contributors',
                    'media',
                  ].includes(scene.id)
                ? scene.id
                : 'flow';
          scene.dataset.buildScene = kind;
          observer.observe(scene);
        }
      scenes = next;
      schedule();
    };
    const motionChanged = () => {
      root.dataset.motion = preference.matches ? 'reduced' : 'full';
      if (preference.matches) {
        cancelAnimationFrame(frame);
        frame = 0;
        scenes.forEach((scene) => {
          scene.style.removeProperty('--scene-enter');
          scene.style.removeProperty('--scene-exit');
        });
      } else schedule();
    };
    const mutations = new MutationObserver(collect);
    mutations.observe(root, { childList: true, subtree: true });
    collect();
    motionChanged();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('visibilitychange', schedule);
    preference.addEventListener('change', motionChanged);
    return () => {
      observer.disconnect();
      mutations.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      document.removeEventListener('visibilitychange', schedule);
      preference.removeEventListener('change', motionChanged);
      scenes.forEach((scene) => {
        delete scene.dataset.buildScene;
        scene.style.removeProperty('--scene-enter');
        scene.style.removeProperty('--scene-exit');
      });
      delete root.dataset.motion;
    };
  }, []);
  return <span ref={marker} hidden aria-hidden="true" />;
}
